import type { Getter, Setter } from 'jotai';
import type { AnswerT } from '../../../../../common/types/types';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import type { AnnotationT } from '../../../../labelling/types';
import { createKeyFrame } from '../../../../labelling/utils';
import { removeDynamicAnswerSegments } from '../../../studio-store/utils/dynamic-answer-utils';
import { toFrameWriteEntries } from '../../../utils/annotation-frame-snapshot';
import { buildAnnotationForOutput } from '../../../utils/build-annotation-for-output';
import { isFrameInAnySpan } from '../../../utils/frame-span-utils';
import { annotationLabelsAtom, annotationMetadataAtom } from '../../annotation-atoms';
import { studioStoreAtom } from '../../studio-store';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { selectedAnnotationIdsAtom } from '../../tool-atoms';
import type { DeleteAnnotationFrameSpanEventPayload } from '../events';
import { reviseDynamicAnswers } from './dynamic-answers-mutation';
import { reviseRelationships } from './revise-relationships-mutation';
export const deleteAnnotationFrameSpanCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<DeleteAnnotationFrameSpanEventPayload, void> => {
  let deletedByFrameIndex: Record<number, AnnotationT> = {};
  let previousSelectedIds: string[] = [];
  let isNextEndFramePromoted = false;
  let restoreDynamicAnswers: () => void = () => {};
  let previousAnswers: AnswerT | undefined;
  let restoreRelationships: () => void = () => {};
  return {
    execute: async payload => {
      const {
        annotationIdx,
        frameSpan
      } = payload;
      const {
        start,
        end
      } = frameSpan;
      const studioStore = get(studioStoreAtom);
      const metadata = get(annotationMetadataAtom).get(annotationIdx);
      const annotationLabels = get(annotationLabelsAtom);
      previousSelectedIds = get(selectedAnnotationIdsAtom);
      previousAnswers = metadata?.answers;
      deletedByFrameIndex = {};
      isNextEndFramePromoted = false;
      restoreDynamicAnswers = reviseDynamicAnswers(set, annotationIdx, metadata?.dynamicAnswers, current => removeDynamicAnswerSegments(current, {
        start,
        end
      }));
      restoreRelationships = reviseRelationships(get, set, annotationIdx, {
        start,
        end
      });
      if (metadata && studioStore) {
        const framePayloads = await studioStore.getAnnotationFramePayloadsInSpans([frameSpan], annotationIdx);
        for (const [frameIndex, framePayload] of framePayloads) {
          deletedByFrameIndex[frameIndex] = buildAnnotationForOutput(framePayload, metadata, annotationLabels, frameIndex);
        }
      }

      // Guard with span check — getAnnotationFrameAtIndex throws if frame not found,
      // so only call it when the annotation is known to span end+1.
      if (studioStore && metadata && isFrameInAnySpan(end + 1, metadata.spans)) {
        const nextFramePayload = await studioStore.getAnnotationFrameAtIndex(end + 1, annotationIdx);
        if (!nextFramePayload.isKeyFrame) {
          isNextEndFramePromoted = true;
          set(dispatchStudioStoreWriteAtom, {
            type: STUDIO_WRITE_ACTIONS.PROMOTE_TO_KEYFRAME,
            frameIndex: end + 1,
            annotationId: annotationIdx
          });
        }
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
        deletes: Array.from({
          length: end - start + 1
        }, (_, i) => ({
          frameIndex: start + i,
          annotationId: annotationIdx
        }))
      });
      if (!payload.preserveSelection) {
        set(selectedAnnotationIdsAtom, previousSelectedIds.filter(id => id !== annotationIdx));
      }
    },
    undo: async payload => {
      const {
        annotationIdx,
        frameSpan
      } = payload;
      const {
        end
      } = frameSpan;
      if (!Object.keys(deletedByFrameIndex).length) {
        restoreDynamicAnswers();
        restoreRelationships();
        return;
      }
      if (isNextEndFramePromoted) {
        const studioStore = get(studioStoreAtom);
        const metadata = get(annotationMetadataAtom).get(annotationIdx);
        const annotationLabels = get(annotationLabelsAtom);
        if (!studioStore || !metadata) {
          return;
        }
        const nextFramePayload = await studioStore.getAnnotationFrameAtIndex(end + 1, annotationIdx);
        const nextAnnotation = buildAnnotationForOutput(nextFramePayload, metadata, annotationLabels, end + 1);
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
          frameIndex: end + 1,
          annotationId: annotationIdx,
          annotation: {
            ...nextAnnotation,
            keyFrame: createKeyFrame(false)
          }
        });
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
        writes: toFrameWriteEntries(annotationIdx, deletedByFrameIndex)
      });
      if (previousAnswers !== undefined) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId: annotationIdx,
          partialPayload: {
            answers: previousAnswers
          }
        });
      }
      restoreDynamicAnswers();
      restoreRelationships();
      if (!payload.preserveSelection) {
        set(selectedAnnotationIdsAtom, previousSelectedIds);
      }
    }
  };
};